/**
 * OptimizedImage - Lazy-loaded image with free CDN proxy (weserv.nl),
 * WebP conversion, blur placeholder, and error handling.
 * Drop-in replacement for <img> tags across the app.
 *
 * Usage: <OptimizedImage src="https://example.com/photo.jpg" width={600} alt="Photo" />
 */
import { useState, useRef, useEffect } from 'react';

const CDN_BASE = 'https://images.weserv.nl/?';

/**
 * Build a weserv.nl CDN URL for automatic WebP conversion and resizing.
 * Only proxies external http(s) URLs. Local/relative paths pass through unchanged.
 */
export function cdnUrl(src, { width, height, quality = 80 } = {}) {
  if (!src || typeof src !== 'string') return src;
  // Only proxy absolute http(s) URLs — skip local/relative/data URIs
  if (!src.startsWith('http://') && !src.startsWith('https://')) return src;
  // Don't double-proxy
  if (src.includes('images.weserv.nl')) return src;

  const params = new URLSearchParams();
  params.set('url', src);
  params.set('output', 'webp');
  if (width) params.set('w', String(width));
  if (height) params.set('h', String(height));
  params.set('q', String(quality));
  params.set('fit', 'cover');
  return `${CDN_BASE}${params.toString()}`;
}

export function OptimizedImage({
  src,
  alt = '',
  className = '',
  width,
  height,
  quality = 80,
  fallback,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);
  const proxied = cdnUrl(src, { width, height, quality });

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          img.src = proxied;
          observer.unobserve(img);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(img);
    return () => observer.disconnect();
  }, [proxied]);

  const handleError = (e) => {
    setError(true);
    // Fallback to original src if CDN fails
    if (!error && src !== proxied) {
      e.target.src = src;
    } else if (fallback) {
      e.target.src = fallback;
    } else {
      e.target.style.display = 'none';
    }
  };

  return (
    <img
      ref={imgRef}
      alt={alt}
      className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={handleError}
      {...props}
    />
  );
}
